import { useEffect, useRef } from "preact/hooks";
import { Fragment } from "preact";
import { createPortal } from "preact/compat";

export function Tools4() {
    const canvasRef = useRef(null);
    const unitRef = useRef(null);
    const inchScaleRef = useRef(null);
    const inchScaleLabelRef = useRef(null);
    const inputX1Ref = useRef(null);
    const inputX2Ref = useRef(null);
    const inputY1Ref = useRef(null);
    const inputY2Ref = useRef(null);
    const setGuidesRef = useRef(null);
    const clearRef = useRef(null);
    const lockRef = useRef(null);
    const downloadRef = useRef(null);

    const leftX = useRef(100);
    const rightX = useRef(250);
    const topY = useRef(100);
    const bottomY = useRef(250);
    const dragging = useRef(false);
    const dragTarget = useRef(null);
    const startCoord = useRef({ x: 0, y: 0 });
    const isGuideLock = useRef(false);

    useEffect(() => {
        const dpi = window.devicePixelRatio || 1;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        const unitSelect = unitRef.current;
        const inchScaleSelect = inchScaleRef.current;
        const inchScaleLabel = inchScaleLabelRef.current;

        const dpiBase = +localStorage.getItem("dpiBase") || 96;

        function getUnitToPx(unit) {
            switch (unit) {
                case "cm":
                    return (dpiBase / 2.54) * dpi;
                case "mm":
                    return (dpiBase / 2.54) * dpi;
                case "in":
                    return dpiBase * dpi;
            }
        }

        function setupCanvas() {
            const width = document
                .querySelector(".ruler-box")
                .getBoundingClientRect().width;
            const height =
                document.querySelector(".ruler-box").getBoundingClientRect()
                    .height - 100;
            canvas.style.width = width + "px";
            canvas.style.height = height + "px";
            canvas.width = width * dpi;
            canvas.height = height * dpi;
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(dpi, dpi);
            ctx.clearRect(0, 0, width, height);
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, canvas.width * 2, canvas.height * 2);
        }

        function drawRulers(unit, inchScale = 16) {
            const unitPx = getUnitToPx(unit) / dpi;
            const divisions = unit === "in" ? inchScale : 10;
            const step = unitPx / divisions;
            const width = document
                .querySelector(".ruler-box")
                .getBoundingClientRect().width;
            const height =
                document.querySelector(".ruler-box").getBoundingClientRect()
                    .height - 100;

            const x1 = Math.min(leftX.current, rightX.current);
            const x2 = Math.max(leftX.current, rightX.current);
            const y1 = Math.min(topY.current, bottomY.current);
            const y2 = Math.max(topY.current, bottomY.current);

            ctx.fillStyle = "rgba(0, 0, 255, 0.2)";
            ctx.fillRect(x1, y1, x2 - x1, y2 - y1);

            ctx.fillStyle = "black";
            ctx.strokeStyle = "black";

            const drawHorizontal = (y) => {
                for (let i = 0; i < width / unitPx; i++) {
                    const baseX = i * unitPx;
                    for (let j = 0; j < divisions; j++) {
                        let x = baseX + j * step;
                        let h = 6,
                            label = "";
                        if (unit === "in") {
                            if (j % (divisions / 2) === 0) {
                                h = 16;
                                if (j === divisions / 2) label = "½";
                            } else if (j % (divisions / 4) === 0) {
                                h = 14;
                                label =
                                    j === divisions / 4
                                        ? "¼"
                                        : j === (3 * divisions) / 4
                                            ? "¾"
                                            : "";
                            } else if (j % (divisions / 8) === 0) h = 12;
                            else if (j % (divisions / 16) === 0) h = 10;
                        } else if (j === 5) h = 12;

                        ctx.beginPath();
                        ctx.moveTo(x, y);
                        ctx.lineTo(x, y + h);
                        ctx.stroke();

                        if (label) {
                            ctx.font = "10px sans-serif";
                            ctx.textAlign = "center";
                            ctx.textBaseline = "top";
                            ctx.fillText(label, x, y + h + 2);
                        }
                    }
                    ctx.beginPath();
                    ctx.moveTo(baseX + unitPx, y);
                    ctx.lineTo(baseX + unitPx, y + 20);
                    ctx.stroke();

                    ctx.font = "11px sans-serif";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "top";
                    const labelText = unit === "mm" ? (i + 1) * 10 : i + 1;
                    ctx.fillText(labelText, baseX + unitPx, y + 24);
                }
            };

            const drawVertical = (x) => {
                for (let i = 0; i < height / unitPx; i++) {
                    const baseY = i * unitPx;
                    for (let j = 0; j < divisions; j++) {
                        let y = baseY + j * step;
                        let w = 6,
                            label = "";
                        if (unit === "in") {
                            if (j % (divisions / 2) === 0) {
                                w = 16;
                                if (j === divisions / 2) label = "½";
                            } else if (j % (divisions / 4) === 0) {
                                w = 14;
                                label =
                                    j === divisions / 4
                                        ? "¼"
                                        : j === (3 * divisions) / 4
                                            ? "¾"
                                            : "";
                            } else if (j % (divisions / 8) === 0) w = 12;
                            else if (j % (divisions / 16) === 0) w = 10;
                        } else if (j === 5) w = 12;

                        ctx.beginPath();
                        ctx.moveTo(x, y);
                        ctx.lineTo(x + w, y);
                        ctx.stroke();

                        if (label) {
                            ctx.font = "10px sans-serif";
                            ctx.textAlign = "left";
                            ctx.textBaseline = "middle";
                            ctx.fillText(label, x + w + 2, y);
                        }
                    }
                    ctx.beginPath();
                    ctx.moveTo(x, baseY + unitPx);
                    ctx.lineTo(x + 20, baseY + unitPx);
                    ctx.stroke();

                    ctx.font = "11px sans-serif";
                    ctx.textAlign = "left";
                    ctx.textBaseline = "middle";
                    const labelText = unit === "mm" ? (i + 1) * 10 : i + 1;
                    ctx.fillText(labelText, x + 24, baseY + unitPx);
                }
            };

            drawHorizontal(0);
            drawVertical(0);

            const unitPxFull = getUnitToPx(unit);
            const wVal =
                (((x2 - x1) * dpi) / unitPxFull) * (unit === "mm" ? 10 : 1);
            const hVal =
                (((y2 - y1) * dpi) / unitPxFull) * (unit === "mm" ? 10 : 1);

            const x1Val = ((x1 * dpi) / unitPxFull) * (unit === "mm" ? 10 : 1);
            const x2Val = ((x2 * dpi) / unitPxFull) * (unit === "mm" ? 10 : 1);
            const y1Val = ((y1 * dpi) / unitPxFull) * (unit === "mm" ? 10 : 1);
            const y2Val = ((y2 * dpi) / unitPxFull) * (unit === "mm" ? 10 : 1);

            ctx.fillStyle = "blue";
            ctx.font = "16px sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            const text = `${wVal.toFixed(2)} × ${hVal.toFixed(2)} ${unit}`;
            ctx.fillText(text, (x1 + x2) / 2, (y1 + y2) / 2);

            inputX1Ref.current.value = x1Val.toFixed(2);
            inputX2Ref.current.value = x2Val.toFixed(2);
            inputY1Ref.current.value = y1Val.toFixed(2);
            inputY2Ref.current.value = y2Val.toFixed(2);
        }

        function render() {
            const unit = unitSelect.value;
            const inchScale = parseInt(inchScaleSelect.value) || 16;
            inchScaleLabel.style.display = unit === "in" ? "inline" : "none";
            setupCanvas();
            drawRulers(unit, inchScale);
        }

        canvas.addEventListener("mousedown", (e) => {
            if (isGuideLock.current) return;

            const { offsetX: x, offsetY: y } = e;
            const margin = 40;

            const isNearLeft = Math.abs(x - leftX.current) <= margin;
            const isNearRight = Math.abs(x - rightX.current) <= margin;
            const isNearTop = Math.abs(y - topY.current) <= margin;
            const isNearBottom = Math.abs(y - bottomY.current) <= margin;

            if (isNearLeft) dragTarget.current = "left";
            else if (isNearRight) dragTarget.current = "right";
            else if (isNearTop) dragTarget.current = "top";
            else if (isNearBottom) dragTarget.current = "bottom";
            else dragTarget.current = null;

            if (dragTarget.current) {
                startCoord.current = { x, y };
                dragging.current = true;
            }
        });

        canvas.addEventListener("mousemove", (e) => {
            if (!dragging.current || !dragTarget.current) return;
            const deltaX = e.offsetX - startCoord.current.x;
            const deltaY = e.offsetY - startCoord.current.y;
            startCoord.current = { x: e.offsetX, y: e.offsetY };

            const width = document
                .querySelector(".ruler-box")
                .getBoundingClientRect().width;
            const height = document
                .querySelector(".ruler-box")
                .getBoundingClientRect().height;

            switch (dragTarget.current) {
                case "left":
                    leftX.current = Math.max(
                        0,
                        Math.min(leftX.current + deltaX, rightX.current - 40)
                    );
                    break;
                case "right":
                    rightX.current = Math.min(
                        width,
                        Math.max(rightX.current + deltaX, leftX.current + 40)
                    );
                    break;
                case "top":
                    topY.current = Math.max(
                        0,
                        Math.min(topY.current + deltaY, bottomY.current - 40)
                    );
                    break;
                case "bottom":
                    bottomY.current = Math.min(
                        height,
                        Math.max(bottomY.current + deltaY, topY.current + 40)
                    );
                    break;
            }
            render();
        });

        ["mouseup", "mouseleave"].forEach((evt) =>
            canvas.addEventListener(evt, () => {
                dragging.current = false;
                dragTarget.current = null;
            })
        );

        // Touch events
        canvas.addEventListener("touchstart", (e) => {
            if (isGuideLock.current) return;

            let { clientX: x, clientY: y } = e.touches[0];
            const margin = 40;

            const rect = canvas.getBoundingClientRect();
            y = y - rect.top;

            const isNearLeft = Math.abs(x - leftX.current) <= margin;
            const isNearRight = Math.abs(x - rightX.current) <= margin;
            const isNearTop = Math.abs(y - topY.current) <= margin;
            const isNearBottom = Math.abs(y - bottomY.current) <= margin;
            y = y + rect.top;

            if (isNearLeft) dragTarget.current = "left";
            else if (isNearRight) dragTarget.current = "right";
            else if (isNearTop) dragTarget.current = "top";
            else if (isNearBottom) dragTarget.current = "bottom";
            else dragTarget.current = null;

            if (dragTarget.current) {
                startCoord.current = { x, y };
                dragging.current = true;
            }

            e.preventDefault();
        });

        canvas.addEventListener("touchmove", (e) => {
            if (!dragging.current || !dragTarget.current) return;
            const deltaX = e.touches[0].clientX - startCoord.current.x;
            const deltaY = e.touches[0].clientY - startCoord.current.y;
            startCoord.current = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
            };

            const width = document
                .querySelector(".ruler-box")
                .getBoundingClientRect().width;
            const height = document
                .querySelector(".ruler-box")
                .getBoundingClientRect().height;

            switch (dragTarget.current) {
                case "left":
                    leftX.current = Math.max(
                        0,
                        Math.min(leftX.current + deltaX, rightX.current - 40)
                    );
                    break;
                case "right":
                    rightX.current = Math.min(
                        width,
                        Math.max(rightX.current + deltaX, leftX.current + 40)
                    );
                    break;
                case "top":
                    topY.current = Math.max(
                        0,
                        Math.min(topY.current + deltaY, bottomY.current - 40)
                    );
                    break;
                case "bottom":
                    bottomY.current = Math.min(
                        height,
                        Math.max(bottomY.current + deltaY, topY.current + 40)
                    );
                    break;
            }
            render();
            e.preventDefault();
        });

        canvas.addEventListener("touchend", () => {
            dragging.current = false;
            dragTarget.current = null;
        });

        canvas.addEventListener("touchcancel", () => {
            dragging.current = false;
            dragTarget.current = null;
        });

        setGuidesRef.current.addEventListener("click", () => {
            const x1Val = parseFloat(inputX1Ref.current.value);
            const x2Val = parseFloat(inputX2Ref.current.value);
            const y1Val = parseFloat(inputY1Ref.current.value);
            const y2Val = parseFloat(inputY2Ref.current.value);

            const width = document
                .querySelector(".ruler-box")
                .getBoundingClientRect().width;
            const height = document
                .querySelector(".ruler-box")
                .getBoundingClientRect().height;

            const unit = unitSelect.value;
            const pxPerUnit = getUnitToPx(unit);

            leftX.current =
                x1Val >= 0 && !isNaN(x1Val)
                    ? ((x1Val / (unit === "mm" ? 10 : 1)) * pxPerUnit) / dpi
                    : 0;
            rightX.current =
                x2Val >= 0 && !isNaN(x2Val)
                    ? ((x2Val / (unit === "mm" ? 10 : 1)) * pxPerUnit) / dpi
                    : 0;
            topY.current =
                y1Val >= 0 && !isNaN(y1Val)
                    ? ((y1Val / (unit === "mm" ? 10 : 1)) * pxPerUnit) / dpi
                    : 0;
            bottomY.current =
                y2Val >= 0 && !isNaN(y2Val)
                    ? ((y2Val / (unit === "mm" ? 10 : 1)) * pxPerUnit) / dpi
                    : 0;

            leftX.current = Math.max(0, Math.min(leftX.current, width));
            rightX.current = Math.max(0, Math.min(rightX.current, width));
            topY.current = Math.max(0, Math.min(topY.current, height));
            bottomY.current = Math.max(0, Math.min(bottomY.current, height));

            render();
        });

        clearRef.current.addEventListener("click", () => {
            leftX.current = 100;
            rightX.current = 250;
            topY.current = 100;
            bottomY.current = 250;

            isGuideLock.current = false;
            render();
        });

        lockRef.current.addEventListener("click", () => {
            isGuideLock.current = !isGuideLock.current;

            if (isGuideLock.current == true) {
                //unlock
                lockRef.current.innerText = langText["text_ruler_unlock"];
            } else {
                //lock
                lockRef.current.innerText = langText["text_ruler_lock"];
            }
        });

        downloadRef.current.addEventListener("click", () => {
            const dataURL = canvas.toDataURL("image/png");

            const link = document.createElement("a");
            link.href = dataURL;
            link.download = "ruler-image.png";
            link.click();
        });

        unitSelect.addEventListener("change", render);
        inchScaleSelect.addEventListener("change", render);
        window.addEventListener("resize", render);

        render();
        return () => {
            window.removeEventListener("resize", render);
            unitSelect.removeEventListener("change", render);
            inchScaleSelect.removeEventListener("change", render);
        };
    }, []);

    return (
        <>
            <canvas ref={canvasRef}></canvas>
            {createPortal(
                <div class="controls tools4" style={{ bottom: "40px", direction: "ltr" }}>
                    <label>
                        {langText["text_ruler_unit"]}:
                        <select ref={unitRef}>
                            <option value="mm">{langText["text_ruler_cm"]}</option>
                            <option value="mm">{langText["text_ruler_mm"]}</option>
                            <option value="mm">{langText["text_ruler_in"]}</option>
                        </select>
                    </label>

                    <label ref={inchScaleLabelRef} style={{ display: "none" }}>
                        {langText["text_ruler_in_scale"]}:
                        <select ref={inchScaleRef}>
                            <option value="2">1/2</option>
                            <option value="4">1/4</option>
                            <option value="8">1/8</option>
                            <option value="16" selected>
                                1/16
                            </option>
                            <option value="32">1/32</option>
                        </select>
                    </label>

                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "1rem",
                        }}
                    >
                        <div>
                            <label>
                                X1:
                                <input
                                    type="number"
                                    ref={inputX1Ref}
                                    style={{ width: "60px" }}
                                />
                            </label>
                            <label>
                                X2:
                                <input
                                    type="number"
                                    ref={inputX2Ref}
                                    style={{ width: "60px" }}
                                />
                            </label>
                        </div>
                        <div s>
                            <label>
                                Y1:
                                <input
                                    type="number"
                                    ref={inputY1Ref}
                                    style={{ width: "60px" }}
                                />
                            </label>
                            <label>
                                Y2:
                                <input
                                    type="number"
                                    ref={inputY2Ref}
                                    style={{ width: "60px" }}
                                />
                            </label>
                        </div>
                        <div>
                            <button ref={setGuidesRef}>{langText["text_ruler_set"]}</button>
                        </div>
                    </div>

                    <div className="action" style={{ marginTop: "10px" }}>
                        <button ref={clearRef}>{langText["text_ruler_clear"]}</button>
                        <button id="lockBtn" ref={lockRef}>{langText["text_ruler_lock"]}</button>
                        <button ref={downloadRef}>
                            {langText["text_ruler_download_iamge"]}
                        </button>
                    </div>
                </div>,
                document.getElementById("ruler-controller")
            )}
        </>
    );
}
